import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, StarHalf, MapPin, PenLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { REVIEWS } from "@/data/reviewsData";
import { fetchApprovedReviews } from "@/lib/api";

export function Stars({ rating }) {
    const full = Math.floor(rating);
    const half = rating % 1 !== 0;
    return (
        <span className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
            {Array.from({ length: full }).map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" />
            ))}
            {half && <StarHalf className="h-3.5 w-3.5 fill-gold text-gold" />}
        </span>
    );
}

function ReviewCard({ r, onOpen }) {
    return (
        <button
            onClick={() => onOpen(r)}
            className="w-80 shrink-0 text-left bg-white rounded-2xl border border-stone-300 p-5 shadow-sm hover:border-saffron hover:shadow-md transition-all cursor-pointer"
            data-testid={`review-card-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        >
            <div className="flex items-center justify-between">
                <Stars rating={r.rating} />
                <span className="text-[10px] font-black uppercase tracking-wider text-saffron bg-saffron/10 rounded-full px-2.5 py-1">{r.type}</span>
            </div>
            <p className="mt-3 text-sm text-stone-700 leading-relaxed line-clamp-3">"{r.text}"</p>
            <div className="mt-4 pt-3 border-t border-stone-100 flex items-center gap-2.5">
                <span className="h-9 w-9 rounded-full bg-navy text-white flex items-center justify-center font-display font-black text-sm">
                    {r.name.charAt(0)}
                </span>
                <div>
                    <div className="text-xs font-black text-navy">{r.name}</div>
                    <div className="text-[11px] text-stone-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{r.city}</div>
                </div>
            </div>
        </button>
    );
}

export function Reviews() {
    const [real, setReal] = useState([]);
    const [active, setActive] = useState(null);

    useEffect(() => {
        fetchApprovedReviews()
            .then((list) => setReal(list.map((r) => ({ name: r.name, city: r.city, type: r.trip_type || "Group Trip", rating: r.rating, text: r.text }))))
            .catch(() => {});
    }, []);

    const items = [...real, ...REVIEWS];

    return (
        <section id="reviews" className="py-20 bg-cream-dark/60 border-b border-stone-300/70 overflow-hidden" data-testid="reviews-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block">pura desh bol raha hai</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl text-navy tracking-tight">Groups Across India Recommend GhumneChaley</h2>
                <p className="mt-3 text-sm text-stone-500 uppercase tracking-wider font-bold">Schools • Colleges • Corporates • Families • Friends</p>
                <p className="mt-1 text-xs text-stone-400">Hover to pause • Tap any review to read it fully</p>
            </div>

            <div className="mt-12 overflow-hidden" data-testid="reviews-marquee">
                <div className="marquee-track flex gap-5 w-max py-2" style={{ animationDuration: "240s" }}>
                    {[...items, ...items].map((r, i) => (
                        <ReviewCard key={`${r.name}-${i}`} r={r} onOpen={setActive} />
                    ))}
                </div>
            </div>

            <div className="mt-10 text-center">
                <Link
                    to="/review"
                    data-testid="share-review-btn"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-navy text-navy text-xs font-display font-black uppercase tracking-wider hover:bg-navy hover:text-white transition-colors"
                >
                    <PenLine className="h-4 w-4" /> Share Your Experience
                </Link>
            </div>

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="max-w-md bg-cream border-stone-300" data-testid="review-dialog">
                    {active && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center justify-between gap-3">
                                    <Stars rating={active.rating} />
                                    <span className="text-[10px] font-black uppercase tracking-wider text-saffron bg-saffron/10 rounded-full px-2.5 py-1">{active.type}</span>
                                </DialogTitle>
                                <DialogDescription className="sr-only">Review by {active.name}</DialogDescription>
                            </DialogHeader>
                            <p className="text-base text-stone-700 leading-relaxed" data-testid="review-dialog-text">"{active.text}"</p>
                            <div className="mt-4 pt-4 border-t border-stone-200 flex items-center gap-3">
                                <span className="h-11 w-11 rounded-full bg-navy text-white flex items-center justify-center font-display font-black text-lg">
                                    {active.name.charAt(0)}
                                </span>
                                <div>
                                    <div className="text-sm font-black text-navy">{active.name}</div>
                                    <div className="text-xs text-stone-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{active.city}</div>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </section>
    );
}
